<template>
  <mu-card id="cholesterol-input" class="aio-input">
    <mu-card-actions class="aio-input-header pointer" @click.native="openInput">
      <mu-flexbox justify="flex_start" style="height: 48px">
        <svg class="card-left-icon" width="16px" height="14px" viewBox="20 193 16 14" version="1.1"
             xmlns="http://www.w3.org/2000/svg">
          <path
            d="M36,197.265908 C36,194.909874 34.0654937,193 31.6791924,193 C30.1209817,193 28.7601736,193.817042 27.9999513,195.038086 C27.2398264,193.817042 25.8784338,193 24.3202556,193 C21.9339542,193 20,194.909874 20,197.265908 C20,198.548637 20.5757115,199.696228 21.4830684,200.477715 L27.6056657,206.522912 C27.7102283,206.626148 27.8520374,206.684147 27.9999513,206.684147 C28.1478652,206.684147 28.2896742,206.626148 28.3942369,206.522912 L34.5168341,200.477715 C35.4242235,199.696228 36,198.548637 36,197.265908 L36,197.265908 Z"
            id="Shape" stroke="none" fill="#D14905" fill-rule="evenodd"></path>
        </svg>
        <mu-flexbox-item class="left16" v-text="title"></mu-flexbox-item>
        <mu-flexbox-item
          align="right"
        >
          <mu-icon-button
            v-if="isOpenInput"
            class="card-bar-icon blueIcon"
            :icon="'remove_circle_outline'"
          />
          <mu-icon-button
            v-tooltip.top="$t('message.addEntryText')"
            v-if="!isOpenInput"
            class="card-bar-icon blueIcon"
            :icon="'add_circle'"
          />
        </mu-flexbox-item>
      </mu-flexbox>
    </mu-card-actions>
    <form v-if="isOpenInput" novalidate>
      <mu-card-text>
        <!-- First row -->
        <mu-row v-if="$store.state.user.settings.blood_glucose.measure == 'mmoll'">
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="12.95"
              hintText="Enter mmol/L"
              :data-vv-rules="'required|min_value:0|max_value:12.95'"
              data-vv-name="total_mmoll"
              data-vv-value-path="inputValue"
              data-vv-as="total"
              :errorText="errors.first('total_mmoll')"
              v-model.number="model.total_mmoll"
              :label="$t('message.labelCholesterolTotal')"
              helpText="mmol/L"
              :fullWidth="true"/>
          </mu-col>
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="12.95"
              hintText="Enter mmol/L"
              :data-vv-rules="'min_value:0|max_value: 12.95'"
              data-vv-name="hdl_mmoll"
              data-vv-value-path="inputValue"
              data-vv-as="hdl"
              :errorText="errors.first('hdl_mmoll')"
              v-model.number="model.hdl_mmoll"
              :label="$t('message.labelCholesterolHDL')"
              helpText="mmol/L"
              :fullWidth="true"/>
          </mu-col>
        </mu-row>
        <mu-row v-if="$store.state.user.settings.blood_glucose.measure == 'mgdl'">
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="700"
              hintText="Enter mg/dL"
              :data-vv-rules="'required|min_value:0|max_value:700'"
              data-vv-name="total_mgdl"
              data-vv-value-path="inputValue"
              data-vv-as="total"
              :errorText="errors.first('total_mgdl')"
              v-model.number="model.total_mgdl"
              :label="$t('message.labelCholesterolTotal')"
              :fullWidth="true"
              helpText="mg/dL"/>
          </mu-col>
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="700"
              hintText="Enter mg/dL"
              :data-vv-rules="'min_value:0|max_value:700'"
              data-vv-name="hdl_mgdl"
              data-vv-value-path="inputValue"
              data-vv-as="hdl"
              :errorText="errors.first('hdl_mgdl')"
              v-model.number="model.hdl_mgdl"
              :label="$t('message.labelCholesterolHDL')"
              :fullWidth="true"
              helpText="mg/dL"/>
          </mu-col>
        </mu-row>
        <!-- End first row -->

        <!-- Second row -->
        <mu-row v-if="$store.state.user.settings.blood_glucose.measure == 'mmoll'" class="bottom">
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              hintText="Enter mmol/L"
              min="0"
              max="18.1"
              :data-vv-rules="'min_value:0|max_value:18.1'"
              data-vv-name="triglycerides_mmoll"
              data-vv-value-path="inputValue"
              data-vv-as="triglycerides"
              :errorText="errors.first('triglycerides_mmoll')"
              v-model.number="model.triglycerides_mmoll"
              :label="$t('message.labelCholesterolTriglycerides')"
              :fullWidth="true"
              helpText="mmol/L"/>
          </mu-col>
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              hintText="Enter mmol/L"
              min="0"
              max="18.1"
              :data-vv-rules="'min_value:0|max_value: 18.1'"
              data-vv-name="ldl_mmoll"
              data-vv-value-path="inputValue"
              data-vv-as="hdl"
              :errorText="errors.first('ldl_mmoll')"
              v-model.number="model.ldl_mmoll"
              :label="$t('message.labelCholesterolLDL')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.blood_glucose.unit"/>
          </mu-col>
        </mu-row>
        <mu-row v-if="$store.state.user.settings.blood_glucose.measure == 'mgdl'" class="bottom">
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              hintText="Enter mg/dL"
              min="0"
              max="700"
              :data-vv-rules="'min_value:0|max_value: 700'"
              data-vv-name="triglycerides_mgdl"
              data-vv-value-path="inputValue"
              data-vv-as="triglycerides"
              :errorText="errors.first('triglycerides_mgdl')"
              v-model.number="model.triglycerides_mgdl"
              :label="$t('message.labelCholesterolTriglycerides')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.blood_glucose.unit"/>
          </mu-col>
          <mu-col width="100" desktop="50" tablet="50">
            <mu-text-field
              type="number"
              v-validate
              min="0"
              max="700"
              hintText="Enter mg\dL"
              :data-vv-rules="'min_value:0|max_value:700'"
              data-vv-name="ldl_mgdl"
              data-vv-value-path="inputValue"
              data-vv-as="hdl"
              :errorText="errors.first('ldl_mgdl')"
              v-model.number="model.ldl_mgdl"
              :label="$t('message.labelCholesterolLDL')"
              :fullWidth="true"
              :helpText="$store.state.user.settings.blood_glucose.unit"/>
          </mu-col>
        </mu-row>
        <!-- End second row -->
      </mu-card-text>
    </form>

  </mu-card>
</template>
<script>
  import config from '../../../../config'
  import ScrollerMixin from '../../../../mixins/ScrollerMixin'

  export default {
    mixins: [ScrollerMixin],
    name: 'aio-cholesterol',
    data() {
      return {
        title: this.$t('message.cardCholesterol'),
        isOpen: false,
        isOpenInput: false,
        isSaving: false,
        isBackgroundShown: true,
        hasData: false,
        noData: false,
        loading: true,
        data: {},
        value: [0, 5],
        settings: {
          blood_glucose: 0
        },
        readingTags: [],
        model: {
          total_mmoll: null,
          ldl_mmoll: null,
          hdl_mmoll: null,
          triglycerides_mmoll: null,
          total_mgdl: null,
          ldl_mgdl: null,
          hdl_mgdl: null,
          triglycerides_mgdl: null,
          tags: []
        }
      }
    },
    watch: {
      'model.total_mmoll': function () {
        this.trySubmitAndEmitValue();
      },
      'model.ldl_mmoll': function () {
        this.trySubmitAndEmitValue();
      },
      'model.hdl_mmoll': function () {
        this.trySubmitAndEmitValue();
      },
      'model.triglycerides_mmoll': function () {
        this.trySubmitAndEmitValue();
      },
      'model.total_mgdl': function () {
        this.trySubmitAndEmitValue();
      },
      'model.ldl_mgdl': function () {
        this.trySubmitAndEmitValue();
      },
      'model.hdl_mgdl': function () {
        this.trySubmitAndEmitValue();
      },
      'model.triglycerides_mgdl': function () {
        this.trySubmitAndEmitValue();
      }
    },
    mounted() {
      let self = this;


    },
    methods: {
      trySubmitAndEmitValue() {
        let self = this;
        self.$validator.validateAll()
          .then(function (valid) {
            if (valid === true) {

              let model = JSON.parse(JSON.stringify(self.model));

              for (let prop in model) {
                if (model.hasOwnProperty(prop)) {
                  if (model[prop] === null || model[prop] === undefined) {
                    delete model[prop];
                  }
                }
              }
              self.$emit('onValid', model)
            }
            else
            {
              self.$emit('onValid', null);
            }
          })
      },
      formatDate(date) {
        return config.dateFormatting.fromNow(date)
      },


      goToDetail() {
        let self = this;
        self.$router.push({
          name: 'CholesterolProgress'
        })
      },
      open() {
        this.isOpen = true
      },
      close() {
        this.isOpen = false
      },
      openInput() {
        let self = this;
        self.isOpenInput = !self.isOpenInput;

        if (self.isOpenInput) {
          self.$gtag.event('Open',{
            'event_category': 'CholesterolCard',
            'event_label': 'Opened card',
            'value': 0
          })
        }
        if (!self.isOpenInput) {
          self.$gtag.event('Closed',{
            'event_category': 'CholesterolCard',
            'event_label': 'Closed card',
            'value': 0
          })
        }

      },
      closeInput() {
        let self = this;
        self.model = {
          tags: []
        };
        self.isOpenInput = false;
        self.$gtag.event('Closed',{
          'event_category': 'CholesterolCard',
          'event_label': 'Closed card',
          'value': 0
        })

      }
    },
    beforeDestroy() {
      this.$EventBus.$off('cholesterolReadingUpdated')
      this.$EventBus.$off('cholesterolPusherReceived');
    }
  }
</script>



// WEBPACK FOOTER //
// Cholesterol.vue?32ebb843